import { OverlayLeaveButton, ResponsiveOverlay } from '@/components/responsive-overlay';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueries } from '@tanstack/react-query';

import type {
  CampaignSyncTarget,
  CreativePreviewStatus,
  CreativeSyncFailureCode,
} from '@/api/manage-hooks';
import { useRetryCreativeSync } from '@/api/manage-hooks';
import { creativePreviewStatus } from '@/api/resources';
import { CADENCE } from '@/api/resource';
import { FailureNotice } from '@/components/failure-notice';
import { Icons } from '@/components/primitives';
import { reportActionError } from '@/lib/report-action-error';

export interface CreativeSyncGateItem {
  id: string;
  label: string;
  target: CampaignSyncTarget;
}

interface CreativeSyncGateProps {
  items: CreativeSyncGateItem[];
  onSynced: () => void | Promise<void>;
  onDismiss: () => void;
  /** When set (campaign context), offers arming the backend to perform the
   *  launch automatically once everything reports ready. */
  onLaunchWhenReady?: () => void | Promise<void>;
}

export type CreativeGateOutcome =
  | 'pending'
  | 'synced'
  | 'local_ready'
  | 'failed'
  | 'wrong_target'
  | 'invalid_terminal';

export interface CreativeCompletionClaim {
  sent: boolean;
}

const FAILURE_LABELS: Record<CreativeSyncFailureCode, string> = {
  provider_authentication: 'Provider authentication failed',
  provider_rate_limited: 'Provider rate limit reached',
  provider_timeout: 'Provider request timed out',
  provider_rejected: 'Provider rejected this creative',
  provider_validation: 'Provider validation failed',
  provider_conflict: 'Provider object did not match',
  provider_unavailable: 'Provider is temporarily unavailable',
  configuration_error: 'Provider configuration is incomplete',
  asset_validation: 'An attached asset is not publishable',
  publication_failed: 'Creative publication failed',
};

export function normalizeCreativeSyncItems(
  items: CreativeSyncGateItem[],
): CreativeSyncGateItem[] {
  const seen = new Set<string>();
  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

function expectedStatus(
  snapshot: CreativePreviewStatus,
  target: CampaignSyncTarget,
): string | null {
  return target === 'meta' ? snapshot.meta_status : snapshot.ttd_status;
}

export function creativeGateOutcome(
  snapshot: CreativePreviewStatus | undefined,
  expectedTarget: CampaignSyncTarget,
): CreativeGateOutcome {
  if (!snapshot) return 'pending';
  if (snapshot.target !== expectedTarget) return 'wrong_target';
  if (!snapshot.terminal) return 'pending';
  if (!snapshot.provider_publishing_enabled) return 'local_ready';

  const status = expectedStatus(snapshot, expectedTarget);
  if (status === 'synced') return 'synced';
  if (status === 'failed') return 'failed';
  return 'invalid_terminal';
}

export function creativeGateComplete(
  items: CreativeSyncGateItem[],
  snapshots: Record<string, CreativePreviewStatus | undefined>,
): boolean {
  return items.length > 0 && items.every((item) => {
    const outcome = creativeGateOutcome(snapshots[item.id], item.target);
    return outcome === 'synced' || outcome === 'local_ready';
  });
}

export function retryableCreativeIds(
  items: CreativeSyncGateItem[],
  snapshots: Record<string, CreativePreviewStatus | undefined>,
): string[] {
  return normalizeCreativeSyncItems(items)
    .filter((item) =>
      creativeGateOutcome(snapshots[item.id], item.target) === 'failed')
    .map((item) => item.id);
}

export function claimCreativeGateCompletion(
  claim: CreativeCompletionClaim,
  complete: boolean,
): boolean {
  if (!complete || claim.sent) return false;
  claim.sent = true;
  return true;
}

function progressFor(
  snapshot: CreativePreviewStatus | undefined,
  target: CampaignSyncTarget,
  outcome: CreativeGateOutcome,
): number {
  if (outcome === 'synced' || outcome === 'local_ready') return 1;
  if (!snapshot) return 0.08;
  const phase = target === 'meta' ? snapshot.meta?.phase : snapshot.ttd?.phase;
  const progress: Record<string, number> = {
    start: 0.12,
    init: 0.12,
    transfer: 0.45,
    upload: 0.45,
    finish: 0.72,
    create: 0.68,
    poll: 0.85,
    done: 1,
  };
  return phase ? progress[phase] ?? 0.18 : 0.08;
}

function outcomeLabel(
  snapshot: CreativePreviewStatus | undefined,
  target: CampaignSyncTarget,
  outcome: CreativeGateOutcome,
): string {
  if (outcome === 'synced') return 'Published';
  if (outcome === 'local_ready') return 'Local ready';
  if (outcome === 'failed') return 'Failed';
  if (outcome === 'wrong_target') return 'Destination mismatch';
  if (outcome === 'invalid_terminal') return 'Invalid terminal state';
  if (!snapshot) return 'Waiting for queue';
  const status = expectedStatus(snapshot, target);
  if (status === 'polling') return 'Provider review';
  if (status === 'syncing') return 'Publishing';
  if (status === 'pending') return 'Queued';
  return 'Preparing';
}

function failureLabel(code: CreativeSyncFailureCode | null): string {
  return code ? FAILURE_LABELS[code] : 'Creative publication failed';
}

export function CreativeSyncGate({
  items,
  onSynced,
  onDismiss,
  onLaunchWhenReady,
}: CreativeSyncGateProps) {
  const retryCreativeSync = useRetryCreativeSync();
  const signature = JSON.stringify(items);
  const stableItems = useMemo(
    () => normalizeCreativeSyncItems(items.map((item) => ({ ...item }))),
    // `signature` makes an equivalent freshly allocated prop stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [signature],
  );
  const gateKey = stableItems
    .map((item) => `${item.id}:${item.target}`)
    .join('|');
  const [actionError, setActionError] = useState<string | null>(null);
  const [retrying, setRetrying] = useState(false);
  const onSyncedRef = useRef(onSynced);
  const completionClaim = useRef<CreativeCompletionClaim>({ sent: false });
  const completionKey = useRef(gateKey);
  onSyncedRef.current = onSynced;
  if (completionKey.current !== gateKey) {
    completionKey.current = gateKey;
    completionClaim.current.sent = false;
  }

  const statusQueries = useQueries({
    queries: stableItems.map((item) => creativePreviewStatus(item.id, {
      cadence: (snapshot) => {
        const outcome = creativeGateOutcome(snapshot, item.target);
        return outcome === 'pending' ? CADENCE.fast : false;
      },
    })),
  });

  const snapshots = useMemo<Record<string, CreativePreviewStatus>>(() => {
    const next: Record<string, CreativePreviewStatus> = {};
    statusQueries.forEach((query, index) => {
      const item = stableItems[index];
      if (item && query.data) next[item.id] = query.data;
    });
    return next;
  }, [stableItems, statusQueries]);

  const queryError = statusQueries.find((query) => query.error)?.error;
  const transportError = actionError ?? (queryError instanceof Error
    ? queryError.message
    : queryError ? String(queryError) : null);

  useEffect(() => {
    const complete = stableItems.length === 0
      || creativeGateComplete(stableItems, snapshots);
    if (claimCreativeGateCompletion(completionClaim.current, complete)) {
      void onSyncedRef.current();
    }
  }, [gateKey, snapshots, stableItems]);

  const retryIds = retryableCreativeIds(stableItems, snapshots);
  const outcomes = stableItems.map((item) => ({
    item,
    snapshot: snapshots[item.id],
    outcome: creativeGateOutcome(snapshots[item.id], item.target),
  }));
  const hasTerminalProblem = outcomes.some(({ outcome }) =>
    outcome === 'failed'
    || outcome === 'wrong_target'
    || outcome === 'invalid_terminal');

  const retry = async () => {
    setRetrying(true);
    setActionError(null);
    try {
      await Promise.all(retryIds.map((id) => retryCreativeSync.mutateAsync(id)));
      await Promise.all(statusQueries
        .filter((_, index) => retryIds.includes(stableItems[index]?.id ?? ''))
        .map((query) => query.refetch()));
    } catch (error) {
      setActionError(reportActionError(error, { operation: 'campaign.creative_sync_retry' }));
    } finally {
      setRetrying(false);
    }
  };

  const checkAgain = async () => {
    setActionError(null);
    try {
      await Promise.all(statusQueries.map((query) => query.refetch()));
    } catch (error) {
      setActionError(reportActionError(error, { operation: 'campaign.creative_sync_check' }));
    }
  };

  return (
    <ResponsiveOverlay label="Publishing progress" kind="progress" className="progress-host" dismissible={false} onClose={onDismiss}>
      <div
        className="launch-dialog"
        style={{ maxHeight: '90vh' }}
        aria-labelledby="creative-sync-title"
        aria-describedby="creative-sync-description"
      >
        <div className="launch-dialog-hd">
          <h3 id="creative-sync-title">
            {hasTerminalProblem
              ? 'Creative publishing needs attention'
              : 'Publishing creatives'}
          </h3>
          <div className="sub" id="creative-sync-description">
            Each approved creative is being prepared for the campaign&apos;s
            actual destination. Publishing continues on the server whether or
            not this dialog stays open — waiting here just advances you the
            moment everything is ready.
          </div>
        </div>
        <div
          className="launch-dialog-body"
          aria-live="polite"
          style={{ overflowY: 'auto' }}
        >
          <div className="lbl">
            {hasTerminalProblem ? 'Terminal failure' : 'Live platform status'}
          </div>
          <div className="launch-dialog-list">
            {outcomes.map(({ item, snapshot, outcome }, index) => {
              const complete = outcome === 'synced' || outcome === 'local_ready';
              const failed = outcome === 'failed'
                || outcome === 'wrong_target'
                || outcome === 'invalid_terminal';
              const progress = progressFor(snapshot, item.target, outcome);
              return (
                <div
                  className="item"
                  key={`${item.id}:${item.target}`}
                  style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}
                >
                  <span
                    className="item-ico"
                    style={{
                      color: failed
                        ? 'var(--bad)'
                        : complete
                          ? 'var(--good)'
                          : 'var(--ink-2)',
                    }}
                  >
                    {complete ? Icons.check : Icons.creatives}
                  </span>
                  <div style={{ flex: '1 1 340px', minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, marginBottom: 8 }}>
                      <b>Creative {index + 1}:</b> {item.label}{' '}
                      <span className="muted">
                        · {item.target === 'meta' ? 'Meta' : 'TTD'}
                      </span>
                    </div>
                    <div style={{ minWidth: 118 }}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 8,
                          fontSize: 10.5,
                          lineHeight: 1.4,
                        }}
                      >
                        <b style={{ color: 'var(--ink)' }}>Publication</b>
                        <span
                          style={{
                            color: failed
                              ? 'var(--bad)'
                              : complete
                                ? 'var(--good)'
                                : 'var(--ink-2)',
                          }}
                        >
                          {outcomeLabel(snapshot, item.target, outcome)}
                        </span>
                      </div>
                      <div
                        aria-hidden="true"
                        style={{
                          height: 3,
                          marginTop: 5,
                          overflow: 'hidden',
                          borderRadius: 99,
                          background: 'var(--line)',
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.round(progress * 100)}%`,
                            height: '100%',
                            borderRadius: 99,
                            background: failed
                              ? 'var(--bad)'
                              : complete
                                ? 'var(--good)'
                                : 'var(--accent)',
                            transition: 'width .25s ease',
                          }}
                        />
                      </div>
                    </div>
                    {outcome === 'failed' && (
                      <div style={{ marginTop: 8 }}>
                        {/* The words, from the server; the footer's
                            "Retry creative sync" is the one Retry here. */}
                        <FailureNotice
                          dense
                          failure={snapshot?.failure}
                          fallback={failureLabel(snapshot?.last_error ?? null)}
                        />
                        {snapshot && snapshot.attempts > 0 && (
                          <div className="muted" style={{ fontSize: 10.5, marginTop: 4 }}>
                            {snapshot.attempts} {snapshot.attempts === 1 ? 'attempt' : 'attempts'}
                          </div>
                        )}
                      </div>
                    )}
                    {outcome === 'wrong_target' && (
                      <div
                        style={{
                          color: 'var(--bad)',
                          fontSize: 10.5,
                          marginTop: 8,
                        }}
                      >
                        The creative&apos;s stored destination does not match
                        this campaign. Return to the draft and replace it.
                      </div>
                    )}
                    {outcome === 'invalid_terminal' && (
                      <div
                        style={{
                          color: 'var(--bad)',
                          fontSize: 10.5,
                          marginTop: 8,
                        }}
                      >
                        The server returned an invalid terminal publication
                        state. No campaign was published.
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {transportError && (
            <div style={{ color: 'var(--bad)', fontSize: 11.5, marginTop: 10 }}>
              Status check failed: {transportError}
            </div>
          )}
        </div>
        <div className="launch-dialog-foot">
          <button className="btn" type="button" onClick={onDismiss}>
            {hasTerminalProblem || transportError
              ? 'Review later'
              : 'Continue in background'}
          </button>
          {onLaunchWhenReady && !hasTerminalProblem && (
            <OverlayLeaveButton
              className="btn btn-primary"
              onLeave={() => {
                void onLaunchWhenReady();
              }}
            >
              Launch automatically when ready
            </OverlayLeaveButton>
          )}
          {retryIds.length > 0 && (
            <button
              className="btn btn-primary"
              type="button"
              disabled={retrying}
              onClick={() => {
                void retry();
              }}
            >
              {Icons.refresh} {retrying ? 'Retrying…' : 'Retry creative sync'}
            </button>
          )}
          {transportError && retryIds.length === 0 && (
            <button
              className="btn btn-primary"
              type="button"
              onClick={() => { void checkAgain(); }}
            >
              {Icons.refresh} Check status again
            </button>
          )}
          {!hasTerminalProblem && !transportError && (
            <span className="muted" style={{ fontSize: 11.5 }}>
              Checking again automatically…
            </span>
          )}
        </div>
      </div>
    </ResponsiveOverlay>
  );
}
